import { useMemo } from 'react';

import { LegalClientBodyEntity } from 'api/types/entity/legal/legalClientBody';
import { getYesOrNoOptions } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { FileType, FileUpload, Stack } from 'libs/ui';
import { getTodayWithResetHours } from 'modules/onboarding/common/helpers/getToday';

import {
  Field,
  MAX_FILES,
  maxLengthPosition,
  useFormContext,
} from '../../../forms/headPositionForm';

export interface FormContentProps {
  files?: FileUpload[];
  bodies?: LegalClientBodyEntity[];
  onlyPositionInCompany?: boolean;
  documentTitle?: string;
  onAddFiles?: (file: File[]) => void;
  onRemoveFile?: (file: FileUpload) => void;
}

export const FormContent = ({
  files,
  bodies = [],
  onlyPositionInCompany,
  documentTitle,
  onAddFiles,
  onRemoveFile,
}: FormContentProps) => {
  const { t } = useTranslation();

  const { values } = useFormContext();

  const optionsBodies = useMemo(() => {
    const optionsBodies = bodies.map((b) => ({
      value: b.id,
      label: b.name,
    }));

    optionsBodies.push({
      label: t('onboarding.headPosition.body.notBelongOrgans'),
      value: '-1',
    });
    return optionsBodies;
  }, [bodies, t]);

  return (
    <Stack spacing={32}>
      {!onlyPositionInCompany && (
        <>
          <Field.Autocomplete
            multiple
            getOptionDisabled={(data) => {
              const option = data as { value: string };
              if (values.bodies?.find((v) => v.value === '-1')) {
                return option.value !== '-1';
              }
              if (values.bodies?.length) {
                return option.value === '-1';
              }

              return false;
            }}
            label={t('onboarding.headPosition.body.label')}
            name="bodies"
            optionLabelKey="label"
            options={optionsBodies}
            optionValueKey="value"
            placeholder={t('onboarding.headPosition.body.placeholder')}
            variant="select"
          />

          <Field.Autocomplete
            hideClearIcon
            label={t('onboarding.headPosition.firstHead.label')}
            name="firstHead"
            optionLabelKey="label"
            options={getYesOrNoOptions(t)}
            optionValueKey="value"
            placeholder={t('onboarding.headPosition.firstHead.placeholder')}
            variant="select"
          />
        </>
      )}

      <Field.Text
        label={t('onboarding.headPosition.position.label')}
        maxLength={maxLengthPosition}
        name="position"
        placeholder={t('onboarding.headPosition.position.placeholder')}
      />

      <Field.DatePicker
        showOptionalMark
        disableFuture={false}
        label={t('onboarding.headPosition.dateOfExpiration.label')}
        minDate={getTodayWithResetHours()}
        name="dateOfExpiration"
        placeholder={t('onboarding.headPosition.dateOfExpiration.placeholder')}
      />

      <Field.FilesUpload
        files={files}
        fileTypes={[FileType.pdf]}
        maxFiles={MAX_FILES}
        name="positionConfirmationFileIds"
        title={documentTitle || t('onboarding.headPosition.documentTitle')}
        onAddFiles={onAddFiles}
        onRemoveFile={onRemoveFile}
      />
    </Stack>
  );
};
